import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import uploadCanvasImage from '@/utils/canvas/uploadCanvasImage';
import type { CanvasImage } from '@/utils/canvas/canvasUtils';
import type { IImageInput, IVideoInput } from '@/types';

type UpdateInputFn = (params: {
  id: string;
  value: Partial<IImageInput | IVideoInput>;
}) => void;

type UploadAndUpdateInputParams = {
  inputId: string;
  image: CanvasImage;
  updateInput: UpdateInputFn;
};

/**
 * Upload a canvas image and update the input with the CDN URL.
 * If the URL is already on Picsart CDN, uploadCanvasImage returns it directly.
 * Falls back to the original URL if upload fails (no auth, network error, etc.).
 */
const uploadAndUpdateInput = async ({
  inputId,
  image,
  updateInput,
}: UploadAndUpdateInputParams): Promise<void> => {
  updateInput({
    id: inputId,
    value: { isLoading: true } as Partial<IImageInput | IVideoInput>,
  });

  try {
    const cdnUrl = await uploadCanvasImage({
      url: image.url,
      imageId: image.id,
    });
    updateInput({
      id: inputId,
      value: { src: cdnUrl ?? image.url, isLoading: false } as Partial<
        IImageInput | IVideoInput
      >,
    });
  } catch (error) {
    errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);
    // Fall back to original URL so the preview is visible
    updateInput({
      id: inputId,
      value: { src: image.url, isLoading: false } as Partial<
        IImageInput | IVideoInput
      >,
    });
  }
};

export default uploadAndUpdateInput;
